import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { consumeIntendedRoute } from "@/lib/authReturnTo";
import { Leaf } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const processed = useRef(false);

  useEffect(() => {
    // useRef synchronous guard (StrictMode-safe) — only valid for one-time exchange
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);

    (async () => {
      try {
        const { data } = await auth.exchange(session_id);
        // Session is held ONLY in the HttpOnly cookie set by the backend.
        // We deliberately do NOT read/persist session_token client-side —
        // that would defeat the HttpOnly protection and expose the token
        // to any script-execution foothold.
        if (data?.user) setUser(data.user);
        // Restore the URL the user was trying to reach when auth kicked them
        // out (e.g. /plant/:id after a swipe-back BFCache restore). Falls
        // back to /home for first-time sign-ins where nothing was stashed.
        const returnTo = consumeIntendedRoute();
        window.history.replaceState(null, "", returnTo);
        navigate(returnTo, { replace: true, state: { user: data?.user } });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("Auth exchange failed", e);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="app-shell flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-leaf">
        <Leaf className="w-7 h-7 animate-pulse" strokeWidth={1.5} />
        <p className="font-display text-xl">{t("authCallback.greeting")}</p>
      </div>
    </div>
  );
}
